import {
  Component,
  inject,
  Inject,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  AfterViewInit
} from '@angular/core';
import {CommonModule, DOCUMENT, isPlatformBrowser} from '@angular/common';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {filter} from "rxjs/operators";
import {AnalyticsType} from "./theme/admin/interfaces/analytics.interface";
import {PublicService} from "./project/client/modules/shared/services/public.service";
import {MetaService} from "./project/client/modules/shared/services/meta.service";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, AfterViewInit {
  private platformId = inject(PLATFORM_ID);

  constructor(
    @Inject(DOCUMENT) private document: any,
    private renderer: Renderer2,
    private publicService: PublicService,
    private router: Router,
    private meta: MetaService
  ) {
  }

  ngOnInit() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        if (this.meta.isPrivatePath(event.urlAfterRedirects)) {
          this.meta.setPrivate();
        }
      });

    if (isPlatformBrowser(this.platformId)) {
      this.publicService.getAnalytics().subscribe(({data}: any) => {
        data.forEach(({type, token, active}: any) => {
          if (type && active) this.renderAnalytics(type, token);
        });
      });
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (performance.navigation.type === 1 || performance.navigation.type === 0) {
        setTimeout(() => {
          if (window.location.hash) {
            const target = this.document.querySelector(window.location.hash);
            if (target) {
              target.scrollIntoView({behavior: 'smooth'});
            }
          }
        }, 2000);
      }
    }
  }

  private renderAnalytics(type: AnalyticsType, key: string) {
    switch (type) {
      case AnalyticsType.FacebookPixel:
        this.loadFacebookPixel(key);
        break;
      case AnalyticsType.GoogleAnalytics:
        this.loadGoogleAnalytics(key);
        break;
    }
  }

  private loadFacebookPixel(pixelId: string): void {
    const script = this.renderer.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)?.[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    this.renderer.appendChild(this.document.head, script);
  }

  private loadGoogleAnalytics(trackingID: string): void {
    const gaScript = this.renderer.createElement('script');
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${trackingID}`;

    const inlineScript = this.renderer.createElement('script');
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${trackingID}');
    `;

    this.renderer.appendChild(this.document.head, gaScript);
    this.renderer.appendChild(this.document.head, inlineScript);
  }
}
